// One photo in the Attached Files card.
//
// Why this exists rather than `<StorageImage>` from @aws-amplify/ui-react-storage:
// showing an attachment is TWO waits, and that component covers neither well. It
// renders `null` while it resolves the signed URL (its `loadingElement` prop
// covers only that phase), then hands the browser an <img> and stops caring --
// so the long wait, downloading a multi-megabyte original from S3, had nothing
// on screen at all. This owns both: a reserved box with the app's skeleton
// shimmer and a spinner until the image has actually decoded.
//
// Two incidental wins from not using it:
//   - `StorageImage` defaults `validateObjectExistence` to true, so every photo
//     cost an extra S3 HEAD ahead of its GET. `storageGet` defaults it to false.
//   - Its `onStorageGetError` was wired to the card's `showError`, so one bad
//     photo replaced the whole card's status banner. Failure is inline now,
//     per photo, and the card is left alone.
//
// Props:
//   imageKey  - the S3 key to DISPLAY. Deliberately separate from whatever the
//               parent navigates to on click, so serving a thumbnail here later
//               is a one-line change at the call site.
//   src       - optional; an ALREADY-RESOLVED URL. Given one, the signed-URL
//               fetch is skipped entirely -- the public shared-trip page has no
//               Amplify Storage to call, and its photos arrive presigned. Absent
//               (the default, and every existing caller) is exactly the previous
//               behaviour. Both waits still apply either way: the <img> is
//               rendered as soon as there is a URL and hidden until it decodes,
//               and an EXPIRED presigned URL fails into the same inline glyph an
//               unreadable S3 object does, which is the point of routing it
//               through here rather than rendering a bare <img>.
//   alt       - alt text (the attachment's file name).
//   sizeBytes - optional; shown under the spinner so a slow photo explains
//               itself. Free -- storageList already carries it.
//   onError   - optional; notified when the photo can't be shown, whichever
//               way it failed. The inline glyph is the user-facing signal, so
//               a caller passing nothing still degrades correctly -- which is
//               what PhotoList does, deliberately: one bad photo must not
//               replace the card's status banner. It is read through a ref,
//               so an inline arrow is safe to pass.

import { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from './Spinner';
import { storageGet } from '../utilities/Database.mjs';

const Loading = 'loading';
const Ready = 'ready';
const Failed = 'failed';

// Human-readable byte count for the placeholder caption. Exported for tests.
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // Whole bytes read oddly as "1.0 B"; everything above gets one decimal.
  const text = unit === 0 ? String(Math.round(value)) : value.toFixed(1);
  return `${text} ${units[unit]}`;
}

export const AttachmentImage = ({ imageKey, src, alt = '', sizeBytes, onError }) => {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState(Loading);

  // Held in a ref so the load effect can depend on `imageKey` ALONE. An inline
  // `onError={(e) => ...}` is a new function identity on every parent render,
  // and with the callback in the deps that would re-run the effect each time --
  // resetting to the placeholder and spending another signed-URL request, on
  // every render. Nothing passes onError today, so this is the latent version
  // of that bug rather than a live one.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Both ways a photo can fail land here, so the `onError` contract -- notified
  // when the photo can't be shown -- is true of the download as well as of the
  // signed-URL fetch. The inline glyph is the user-facing signal either way;
  // this is only for a caller that wants to know.
  const fail = useCallback((err) => {
    setState(Failed);
    if (onErrorRef.current) {
      onErrorRef.current(err);
    }
  }, []);

  // NOTE: src/pages/item/useStorageURL.jsx is a near-duplicate of this effect,
  // but it lives under pages/ (components/ reaching into it would be backwards),
  // hard-codes the validateObjectExistence round trip we're dropping, and logs
  // on every load. The two should be consolidated into src/utilities/ later.
  useEffect(() => {
    let ignore = false;

    setUrl(null);
    setState(Loading);

    // An already-resolved URL: nothing to fetch, and deliberately nothing else
    // skipped -- the decode gate below still runs, so a slow or dead presigned
    // URL behaves exactly as a slow or unreadable S3 object does.
    if (src) {
      setUrl(src);
      return undefined;
    }

    if (!imageKey) {
      return undefined;
    }

    storageGet(imageKey)
      .then((file) => {
        if (ignore) {
          return;
        }
        setUrl(file.url.toString());
      })
      .catch((err) => {
        if (ignore) {
          return;
        }
        fail(err);
      });

    return () => {
      ignore = true;
    };
  }, [imageKey, src, fail]);

  return (
    <span className="tp-attachment-thumb" aria-busy={state === Loading}>
      {url && (
        // Rendered as soon as the URL lands but HIDDEN until it has decoded --
        // via `visibility`, never `display: none`, which would opt the image
        // out of lazy loading and deprioritise its decode.
        <img
          src={url}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={state === Ready ? undefined : 'is-loading'}
          onLoad={() => setState(Ready)}
          onError={() => fail(new Error(`Could not load image ${imageKey}`))}
        />
      )}

      {state === Loading && (
        <>
          {/* The skeleton is decoration: Spinner already carries role="status"
              and a visually-hidden label, so the block is aria-hidden, per the
              convention documented above .tp-skeleton in App.css. */}
          <span className="tp-skeleton tp-attachment-thumb-skeleton" aria-hidden="true" />
          <span className="tp-attachment-thumb-overlay">
            <Spinner label={alt ? `Loading ${alt}` : 'Loading image'} />
            {sizeBytes ? (
              <span className="tp-attachment-thumb-size">{formatBytes(sizeBytes)}</span>
            ) : null}
          </span>
        </>
      )}

      {state === Failed && (
        <span className="tp-attachment-thumb-overlay">
          <i className="fa-regular fa-image fa-2x" aria-hidden="true"></i>
          <span className="visually-hidden">
            {alt ? `${alt} could not be loaded` : 'Image could not be loaded'}
          </span>
        </span>
      )}
    </span>
  );
};

export default AttachmentImage;
